import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../../../lib/supabaseClient";
import type { ConversationMessage, ConversationThreadView, ConversationParticipantType } from "../../../store/types";
import RelayThreadList from "./components/RelayThreadList";
import RelayMessagePanel from "./components/RelayMessagePanel";
import RelayOrderSummary from "./components/RelayOrderSummary";
import AdminLayout from "../components/AdminLayout";

interface CreateModalState {
  open: boolean;
  orderId: string;
  participantType: ConversationParticipantType;
  subject: string;
}

async function logRelayEvent(threadId: string, eventType: string, actorId: string, payload: Record<string, unknown>) {
  if (!supabase) return;
  await supabase.from("relay_events").insert([{ thread_id: threadId, event_type: eventType, actor_type: "admin", actor_id: actorId, payload }]);
}

export default function AdminRelayPage() {
  const [searchParams] = useSearchParams();
  const [threads, setThreads] = useState<ConversationThreadView[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get("thread"));
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [threadsError, setThreadsError] = useState<string | null>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [createModal, setCreateModal] = useState<CreateModalState>({
    open: false, orderId: "", participantType: "customer", subject: "",
  });
  const [orderSearchResults, setOrderSearchResults] = useState<{ id: string; order_number: string; customer_name: string }[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const selectedThread = threads.find((t) => t.id === selectedId) ?? null;

  const fetchThreads = useCallback(async () => {
    if (!supabase) return;
    setLoadingThreads(true);
    setThreadsError(null);
    try {
      const { data, error } = await supabase
        .from("conversation_threads")
        .select("*, orders(order_number, customer_name, shoot_date, selected_city)")
        .order("updated_at", { ascending: false });

      if (error) throw error;

      if (data) {
        const enriched: ConversationThreadView[] = (data as Record<string, unknown>[]).map((row) => {
          const order = row.orders as Record<string, string> | null;
          return {
            id: row.id as string,
            order_id: row.order_id as string | null,
            participant_type: row.participant_type as ConversationThreadView["participant_type"],
            subject: row.subject as string,
            status: row.status as ConversationThreadView["status"],
            risk_level: (row.risk_level as ConversationThreadView["risk_level"]) ?? "none",
            risk_summary: (row.risk_summary as string) ?? "",
            created_at: row.created_at as string,
            updated_at: row.updated_at as string,
            order_number: order?.order_number,
            customer_name: order?.customer_name,
            shoot_date: order?.shoot_date,
            selected_city: order?.selected_city,
          };
        });
        setThreads(enriched);
      }
    } catch (e) {
      setThreadsError(e instanceof Error ? e.message : "載入訊息串失敗");
    } finally {
      setLoadingThreads(false);
    }
  }, []);

  const fetchMessages = useCallback(async (threadId: string) => {
    if (!supabase) return;
    setLoadingMessages(true);
    const { data } = await supabase
      .from("conversation_messages")
      .select("*")
      .eq("thread_id", threadId)
      .order("created_at", { ascending: true });
    if (data) setMessages(data as ConversationMessage[]);
    setLoadingMessages(false);
  }, []);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  useEffect(() => {
    if (!selectedId) { setMessages([]); return; }
    fetchMessages(selectedId);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchMessages(selectedId), 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedId, fetchMessages]);

  const handleSend = async (body: string, messageType: ConversationMessage["message_type"] = "text") => {
    if (!supabase || !selectedId) return;
    await supabase.from("conversation_messages").insert([{
      thread_id: selectedId,
      sender_type: "admin",
      sender_id: "admin",
      body,
      message_type: messageType,
      is_read_admin: true,
    }]);
    await supabase.from("conversation_threads").update({ updated_at: new Date().toISOString() }).eq("id", selectedId);
    await fetchMessages(selectedId);
    await fetchThreads();
  };

  const handleFlag = async (threadId: string, riskLevel: string, reason: string) => {
    if (!supabase) return;
    await supabase.from("conversation_threads").update({
      risk_level: riskLevel,
      risk_summary: reason,
      status: "flagged",
    }).eq("id", threadId);
    await logRelayEvent(threadId, "risk_flagged", "admin", { risk_level: riskLevel, reason });
    await fetchThreads();
  };

  const handleChangeStatus = async (threadId: string, status: "open" | "closed" | "flagged") => {
    if (!supabase) return;
    await supabase.from("conversation_threads").update({ status }).eq("id", threadId);
    await logRelayEvent(threadId, "status_changed", "admin", { status });
    await fetchThreads();
  };

  const handleSendSystemNotice = async (body: string) => {
    await handleSend(body, "system_notice");
  };

  const handleOrderSearch = async (q: string) => {
    if (!supabase || q.length < 2) { setOrderSearchResults([]); return; }
    const { data } = await supabase.from("orders").select("id, order_number, customer_name").ilike("order_number", `%${q}%`).limit(6);
    if (data) setOrderSearchResults(data as { id: string; order_number: string; customer_name: string }[]);
  };

  const handleCreateThread = async () => {
    if (!supabase || !createModal.subject.trim()) return;
    const { data } = await supabase
      .from("conversation_threads")
      .insert([{ order_id: createModal.orderId || null, participant_type: createModal.participantType, subject: createModal.subject.trim() }])
      .select()
      .single();
    if (data) {
      const thread = data as { id: string };
      await logRelayEvent(thread.id, "thread_created", "admin", { participant_type: createModal.participantType });
      setCreateModal({ open: false, orderId: "", participantType: "customer", subject: "" });
      setOrderSearchResults([]);
      await fetchThreads();
      setSelectedId(thread.id);
    }
  };

  return (
    <AdminLayout>
      <div className="flex h-[calc(100vh-64px)] -m-6 overflow-hidden">
        <RelayThreadList
          threads={threads}
          selectedId={selectedId}
          onSelect={setSelectedId}
          loading={loadingThreads}
          error={threadsError}
          onRetry={fetchThreads}
          onCreateThread={() => setCreateModal((p) => ({ ...p, open: true }))}
        />
        <RelayMessagePanel thread={selectedThread} messages={messages} loadingMessages={loadingMessages} onSend={handleSend} onFlag={handleFlag} onChangeStatus={handleChangeStatus} />
        <RelayOrderSummary thread={selectedThread} onSendSystemNotice={handleSendSystemNotice} />
      </div>

      {createModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-sm font-medium text-[#2C2C2C] mb-4">建立訊息串</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#2C2C2C]/60 font-light block mb-1">訊息類型</label>
                <select value={createModal.participantType} onChange={(e) => setCreateModal((p) => ({ ...p, participantType: e.target.value as ConversationParticipantType }))}
                  className="w-full px-3 py-2 text-xs font-light border border-[#E8E0D5] rounded-lg bg-[#F7F6F3] text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] text-sm">
                  <option value="customer">客戶溝通</option>
                  <option value="photographer">攝影師溝通</option>
                  <option value="internal">內部</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[#2C2C2C]/60 font-light block mb-1">主旨</label>
                <input type="text" value={createModal.subject} onChange={(e) => setCreateModal((p) => ({ ...p, subject: e.target.value }))} placeholder="訊息主旨..."
                  className="w-full px-3 py-2 text-xs font-light border border-[#E8E0D5] rounded-lg bg-[#F7F6F3] text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#2C2C2C]/60 font-light block mb-1">關聯訂單（選填）</label>
                <input type="text" placeholder="搜尋訂單編號..." onChange={(e) => handleOrderSearch(e.target.value)}
                  className="w-full px-3 py-2 text-xs font-light border border-[#E8E0D5] rounded-lg bg-[#F7F6F3] text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] text-sm" />
                {orderSearchResults.length > 0 && (
                  <div className="mt-1 border border-[#E8E0D5] rounded-lg overflow-hidden">
                    {orderSearchResults.map((o) => (
                      <button key={o.id} onClick={() => { setCreateModal((p) => ({ ...p, orderId: o.id })); setOrderSearchResults([]); }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-[#F7F6F3] cursor-pointer transition-colors ${createModal.orderId === o.id ? "bg-[#F7F6F3]" : "text-[#2C2C2C]/70"}`}>
                        {o.order_number} — {o.customer_name}
                      </button>
                    ))}
                  </div>
                )}
                {createModal.orderId && (
                  <div className="mt-1 flex items-center gap-1">
                    <span className="text-[10px] text-emerald-600">✓ 已選擇訂單</span>
                    <button onClick={() => setCreateModal((p) => ({ ...p, orderId: "" }))} className="text-[10px] text-[#2C2C2C]/30 cursor-pointer">清除</button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setCreateModal({ open: false, orderId: "", participantType: "customer", subject: "" }); setOrderSearchResults([]); }}
                className="flex-1 py-2 rounded-lg border border-[#E8E0D5] text-xs text-[#2C2C2C]/60 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleCreateThread} disabled={!createModal.subject.trim()}
                className="flex-1 py-2 rounded-lg bg-[#2C2C2C] text-white text-xs disabled:opacity-40 hover:bg-[#444] transition-colors cursor-pointer whitespace-nowrap">建立</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
